/* jp-cart-empty.js — branded empty-cart state (S33) for BOTH the cart DRAWER
   and the /cart PAGE. Detects Dawn's empty markup and injects the branded panel
   (cloned from <template data-jp-cart-empty-template>) in place of Dawn's default.

   Robustness:
   - Re-runs after every Dawn cart re-render by (a) subscribing to
     PUB_SUB_EVENTS.cartUpdate, (b) observing #cart-icon-bubble mutations, and
     (c) listening for the counterpart-cart DOM completion event from cart.js.
   - NEVER shows the panel when the cart has items: we only inject into a host
     that Dawn itself marked empty (.drawer__inner-empty / cart-items.is-empty
     with no line items), and we remove our panel the moment that host is gone.
   - No-JS / template-less: Dawn's default empty state is left in the DOM and
     stays visible (we only hide it via the .jp-cempty-on host class), so it is
     the graceful fallback.
   - Idempotent + guarded so a second include or a re-init can't double-inject. */
(() => {
  if (window.__jpCartEmptyInit) return;
  window.__jpCartEmptyInit = true;

  const pubSubEvents = () => (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);

  const tpl = () => document.querySelector('template[data-jp-cart-empty-template]');
  const style = document.querySelector('[data-jp-cart-empty-style]');
  const styleReady = () => !style || style.dataset.loaded === 'true' || style.media === 'all';

  // Build a fresh panel node from the template (returns null if template absent).
  const build = () => {
    const t = tpl();
    if (!t || !t.content) return null;
    const frag = t.content.cloneNode(true);
    return frag.querySelector('[data-jp-cempty]');
  };

  // DRAWER empty host: cart-drawer's .drawer__inner-empty exists ONLY when Dawn
  // rendered the empty drawer. Inject into its .cart-drawer__empty-content
  // (keeps Dawn's own close button + collection card intact), else the wrapper.
  const drawerHost = () => {
    const empty = document.querySelector('cart-drawer .drawer__inner-empty');
    if (!empty) return null;
    return {
      mount: empty.querySelector('.cart-drawer__empty-content') || empty,
      flag: empty.querySelector('.cart-drawer__empty-content') || empty,
    };
  };

  // PAGE empty host: Dawn puts the empty copy in .cart__warnings and marks
  // <cart-items> / <cart-drawer-items> with .is-empty. Treat as empty when the
  // is-empty class is present OR there are no rendered line-item rows.
  const pageHost = () => {
    const warnings = document.querySelector('.cart__warnings');
    if (!warnings) return null;
    const items = warnings.closest('cart-items'); // page's own element (not the drawer's, which precedes it in the DOM)
    // On the /cart page, cart-items carries .is-empty when empty. Also guard on
    // the absence of any .cart-item row inside the contents.
    const isEmpty = items
      ? items.classList.contains('is-empty') || !items.querySelector('.cart-item')
      : !document.querySelector('.cart-item');
    if (!isEmpty) return null;
    return { mount: warnings, flag: warnings };
  };

  // Inject (or refresh) our panel into a host; mark the host so CSS hides Dawn's default.
  const applyTo = (host) => {
    if (!host || !host.mount) return;
    const focusWasInHost = host.flag.contains(document.activeElement);
    if (host.mount.querySelector('[data-jp-cempty]')) {
      host.flag.classList.add('jp-cempty-on');
      return; // already present
    }
    const panel = build();
    if (!panel) return; // no template → leave Dawn's default (no-JS fallback path)
    host.mount.appendChild(panel);
    host.flag.classList.add('jp-cempty-on');
    /* A last-line remove intentionally focuses Dawn's empty-state link. Once
       this enhancement hides that fallback, transfer focus to the equivalent
       branded CTA instead of letting it fall back to <body>. In an open drawer
       rebuild the trap too, because its focusable-node snapshot predates this
       injected panel. */
    if (focusWasInHost) {
      const focusTarget = panel.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusTarget) {
        const drawer = host.flag.closest('cart-drawer.active');
        const drawerContainer = drawer?.querySelector('.drawer__inner-empty');
        if (drawer && drawerContainer && typeof trapFocus === 'function') trapFocus(drawerContainer, focusTarget);
        else {
          try { focusTarget.focus({ preventScroll: true }); } catch { focusTarget.focus(); }
        }
      }
    }
  };

  // Remove our panel from a container that is no longer an empty host (cart now
  // has items, or Dawn re-rendered a fresh non-empty drawer).
  const cleanup = (scope) => {
    (scope || document).querySelectorAll('.jp-cempty-on').forEach((el) => {
      // keep it only if this element is still a live empty host
      const stillEmpty =
        (el.closest('cart-drawer .drawer__inner-empty')) ||
        (el.classList.contains('cart__warnings') && pageHost());
      if (!stillEmpty) {
        el.querySelectorAll('[data-jp-cempty]').forEach((p) => p.remove());
        el.classList.remove('jp-cempty-on');
      }
    });
  };

  const render = () => {
    // On non-cart routes this presentation sheet is deliberately non-blocking.
    // Keep Dawn's fully styled fallback until the enhancement CSS is ready.
    if (!styleReady()) return;
    // Order matters: clean up stale panels first (cart just gained items), then
    // (re)inject into whichever empty host is currently on the page.
    cleanup();
    const dh = drawerHost();
    if (dh) applyTo(dh);
    const ph = pageHost();
    if (ph) applyTo(ph);
  };

  let timer;
  const schedule = () => { window.clearTimeout(timer); timer = setTimeout(render, 60); };

  const boot = () => {
    if (style && !styleReady()) {
      style.addEventListener('load', () => {
        style.media = 'all';
        style.dataset.loaded = 'true';
        render();
      }, { once: true });
    }
    render();

    // (a) Dawn's cartUpdate pub/sub — fires on add/remove/qty change everywhere.
    const events = pubSubEvents();
    if (typeof subscribe === 'function' && events?.cartUpdate) {
      subscribe(events.cartUpdate, schedule);
    }

    // The pub/sub notification starts the counterpart page/drawer refresh; it
    // does not mean that asynchronous replacement has finished. cart.js emits
    // this event after that replacement so both surfaces receive the branded
    // empty state when the final line is removed from either one.
    document.addEventListener('jp:cart-dom-updated', schedule);

    // (b) #cart-icon-bubble mutations — catches the drawer open + any AJAX cart
    //     section re-render (the drawer's inner HTML is swapped wholesale).
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) new MutationObserver(schedule).observe(bubble, { childList: true, subtree: true });

    // (c) Drawer inner is re-rendered by Dawn's cart-drawer.js on every update;
    //     observe the cart-drawer element so a freshly-rendered empty drawer
    //     (e.g. removing the last item while the drawer is open) is caught even
    //     if the bubble text didn't change structurally.
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Re-init on section reloads (theme editor / Dawn section rendering).
  document.addEventListener('shopify:section:load', schedule);
})();
